import React, { useState, useEffect } from 'react';
import './AddTicketCreationExtraTimeComponent.css';

/**
 * AddTicketCreationExtraTimeComponent
 * 
 * Allows users to add extra time DURING ticket creation
 * Time entries are stored locally and submitted when user clicks the main submit button
 * This component DOES NOT submit directly - it only collects entries
 */
const AddTicketCreationExtraTimeComponent = ({ onTimeEntriesChange }) => {
  const [timeEntries, setTimeEntries] = useState([]);
  const [category, setCategory] = useState('Call Time');
  const [timeMinutes, setTimeMinutes] = useState('');
  const [description, setDescription] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // 'success', 'error', 'info'

  // Category options for time tracking
  const timeCategories = [
    { value: 'Call Time', label: '📞 Call Time (Customer conversation)' },
    { value: 'Gathering Information', label: '📋 Gathering Information' },
    { value: 'Research', label: '🔍 Research & Investigation' },
    { value: 'Analysis', label: '📊 Analysis & Understanding' },
    { value: 'Troubleshooting', label: '🔧 Troubleshooting' },
    { value: 'Other', label: '⏱️ Other' }
  ];

  // Notify parent component whenever time entries change
  useEffect(() => {
    if (onTimeEntriesChange) {
      onTimeEntriesChange(timeEntries);
    }
  }, [timeEntries, onTimeEntriesChange]);

  // Show message
  const showMessage = (text, type) => {
    setMessage(text);
    setMessageType(type);
    if (type !== 'error') {
      setTimeout(() => {
        setMessage('');
        setMessageType('');
      }, 3000);
    }
  };

  // Validate inputs
  const validateInput = () => {
    if (!timeMinutes || isNaN(timeMinutes)) {
      showMessage('Please enter a valid time in minutes', 'error');
      return false;
    }

    const minutes = parseInt(timeMinutes);
    if (minutes < 1 || minutes > 480) {
      showMessage('Time must be between 1 and 480 minutes (8 hours)', 'error');
      return false;
    }

    if (!category.trim()) {
      showMessage('Please select a category', 'error');
      return false;
    }

    return true;
  };

  // Add time entry to local list
  const handleAddEntry = () => {
    if (!validateInput()) return;

    const newEntry = {
      id: Date.now(),
      category: category,
      timeMinutes: parseInt(timeMinutes),
      description: description.trim()
    };

    setTimeEntries([...timeEntries, newEntry]);
    setTimeMinutes('');
    setDescription('');
    setCategory('Call Time');
    showMessage(`Added ${newEntry.timeMinutes} minutes for "${newEntry.category}"`, 'success');
  };

  // Remove entry from list
  const handleRemoveEntry = (id) => {
    setTimeEntries(timeEntries.filter(entry => entry.id !== id));
    showMessage('Entry removed', 'info');
  };

  // Calculate total time
  const totalTime = timeEntries.reduce((sum, entry) => sum + entry.timeMinutes, 0);

  return (
    <div className="add-ticket-creation-time-container bg-light">
      <div className="time-component-header">
        <h4 className="text-dark">⏱️ Add Extra Time for Ticket Creation</h4>
        <small className="text-muted">Track time spent on calls, gathering info, and research</small>
      </div>

      {/* Message Display */}
      {message && (
        <div className={`alert alert-${messageType} alert-dismissible fade show`} role="alert">
          {message}
          <button
            type="button"
            className="btn-close"
            onClick={() => setMessage('')}
          ></button>
        </div>
      )}

      {/* Input Section */}
      <div className="time-input-section card mb-3">
        <div className="card-body">
          <div className="row mb-3">
            <div className="col-md-4">
              <label className="form-label">Time Category *</label>
              <select
                className="form-select"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                {timeCategories.map(cat => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label">Time (minutes) *</label>
              <input
                type="number"
                className="form-control"
                placeholder="e.g., 15"
                value={timeMinutes}
                onChange={(e) => setTimeMinutes(e.target.value)}
                min="1"
                max="480"
              />
              <small className="text-muted">1-480 minutes (max 8 hours)</small>
            </div>

            <div className="col-md-5">
              <label className="form-label">Description (optional)</label>
              <input
                type="text"
                className="form-control"
                placeholder="Add more details..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
          </div>

          <button
            className="btn btn-primary btn-sm"
            onClick={handleAddEntry}
          >
            ➕ Add Entry
          </button>
        </div>
      </div>

      {/* Time Entries List */}
      {timeEntries.length > 0 && (
        <div className="time-entries-section">
          <div className="entries-header">
            <h5>📝 Time Entries ({timeEntries.length})</h5>
            <span className="badge bg-info">Total: {totalTime} min</span>
          </div>

          <div className="entries-list">
            {timeEntries.map((entry) => (
              <div key={entry.id} className="entry-card">
                <div className="entry-content">
                  <div className="entry-category">
                    <strong>{entry.category}</strong>
                  </div>
                  <div className="entry-time">
                    <span className="badge bg-primary">{entry.timeMinutes} min</span>
                  </div>
                  {entry.description && (
                    <div className="entry-description">
                      <small>{entry.description}</small>
                    </div>
                  )}
                </div>
                <button
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => handleRemoveEntry(entry.id)}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>

          {/* Submit Button */}
          <div className="submit-section mt-3">
            <small className="d-block text-info">
              ✓ {totalTime} minutes collected. These will be saved with the ticket!
            </small>
          </div>
        </div>
      )}

      {/* Tips Section */}
      <div className="tips-section mt-3">
        <details>
          <summary className="text-muted" style={{ cursor: 'pointer' }}>
            💡 Tips for time tracking
          </summary>
          <ul className="mt-2">
            <li><strong>Call Time:</strong> Time spent on phone/video call with customer</li>
            <li><strong>Gathering Information:</strong> Time collecting details about the issue</li>
            <li><strong>Research:</strong> Time spent investigating or looking up information</li>
            <li><strong>Analysis:</strong> Time understanding the problem and planning solution</li>
            <li><strong>Troubleshooting:</strong> Time trying different solutions</li>
            <li><strong>Other:</strong> Any other time related to ticket creation</li>
          </ul>
        </details>
      </div>
    </div>
  );
};

export default AddTicketCreationExtraTimeComponent;
